import React, { useState } from "react";
import type { TCurrentVessel } from "@/types";
import SearchVessel from "./SearchVessel";
import ApiClient from "../services/fuelandmoreapi";
import RiskScoreDetailsModal from "./RiskScoreDetailsModal";
import "./AddVessel.css";
import { config } from "../config";

interface AddVesselProps {
  fleetName: string | null;
  clientFleetId: string;
  onCancel: () => void;
  onAddVessel: (clientFleetId: string, newVessel: TCurrentVessel) => void;
}

const AddVessel: React.FC<AddVesselProps> = ({
  fleetName,
  clientFleetId,
  onCancel,
  onAddVessel,
}) => {
  const [selectedVessel, setSelectedVessel] = useState<TCurrentVessel | null>(
    null
  );
  const [loading, setLoading] = useState(false);
  const [riskScore, setRiskScore] = useState<number | null>(null);
  const [riskScoreJson, setRiskScoreJson] = useState<any>(null);
  const [riskDialogOpen, setRiskDialogOpen] = useState(false);
  const [riskScoreLoading, setRiskScoreLoading] = useState(false);
  const [vesselTableExpanded, setVesselTableExpanded] = useState(true); // NEW STATE

  const { apiBaseUrl } = config;

  // Fetch risk score when selectedVessel changes
  React.useEffect(() => {
    const fetchRiskScore = async () => {
      if (!selectedVessel) {
        setRiskScore(null);
        setRiskScoreJson(null);
        setRiskScoreLoading(false);
        return;
      }
      setRiskScoreLoading(true);
      try {
        const apiClient = new ApiClient(apiBaseUrl);
        const result = await apiClient.getVesselRiskScore(selectedVessel.imo);
        const scoreObj = result.data.RiskScores.find(
          (r: any) => r.RiskPeriodInMonths === 6
        );
        const score = scoreObj ? scoreObj.TotalRiskScore : null;
        setRiskScore(typeof score === "number" ? score : null);
        setRiskScoreJson(result);
      } catch (error) {
        setRiskScore(null);
        setRiskScoreJson(null);
      } finally {
        setRiskScoreLoading(false);
      }
    };
    fetchRiskScore();
  }, [selectedVessel, apiBaseUrl]);

  // Collapse vessel table when a vessel is selected
  React.useEffect(() => {
    if (selectedVessel) {
      setVesselTableExpanded(false);
    }
  }, [selectedVessel]);

  // Handler for search field focus
  const handleSearchFocus = () => {
    if (selectedVessel) {
      setSelectedVessel(null); // Clear the selected vessel
    }
    setVesselTableExpanded(true); // Always expand the table
  };

  const handleAddVessel = async () => {
    if (!selectedVessel) return;

    setLoading(true);
    try {
      const apiClient = new ApiClient(`${apiBaseUrl}`);
      await apiClient.addVesselToClientFleet(clientFleetId, selectedVessel.imo);
      onAddVessel(clientFleetId, selectedVessel);
      console.log("Vessel successfully added to the fleet:", selectedVessel);
      onCancel();
    } catch (error) {
      console.error("Error adding vessel to fleet:", error);
    } finally {
      setLoading(false);
    }
  };

  // Helper for risk score color
  const getRiskColor = (score: number | null) => {
    if (score === null || score === undefined) return "";
    if (score > 70) return "risk-score-red";
    if (score > 30) return "risk-score-amber";
    return "risk-score-green";
  };

  const getRiskScoreText = (score: number | null) => {
    if (score === null || score === undefined) return "N/A";
    if (score > 70) return "High";
    if (score > 30) return "Medium";
    return "Low";
  };

  return (
    <div className="add-vessel-container">
      <h2 className="add-vessel-title">Add Vessel to {fleetName}</h2>
      <div className="add-vessel-dialog-paper">
        <div className="add-vessel-dialog-content">
          <SearchVessel
            onSelectVessel={(vessel) => setSelectedVessel(vessel)}
            onSearchFocus={handleSearchFocus}
            vesselTableExpanded={vesselTableExpanded}
          />
          {selectedVessel && (
            <form
              className="add-vessel-selected add-vessel-form"
              onSubmit={(e) => {
                e.preventDefault();
                handleAddVessel();
              }}
            >
              <div className="add-vessel-details-row">
                <div className="add-vessel-form-group">
                  <label htmlFor="imo">IMO:</label>
                  <span id="imo" className="add-vessel-value">
                    {selectedVessel.imo || ""}
                  </span>
                </div>
                <div className="add-vessel-form-group">
                  <label htmlFor="name">Name:</label>
                  <span id="name" className="add-vessel-value">
                    {selectedVessel.name || ""}
                  </span>
                </div>
                <div className="add-vessel-form-group">
                  <label htmlFor="type">Type:</label>
                  <span id="type" className="add-vessel-value">
                    {selectedVessel.type || ""}
                  </span>
                </div>
                <div className="add-vessel-form-group">
                  <label htmlFor="dwt">DWT:</label>
                  <span id="dwt" className="add-vessel-value">
                    {selectedVessel.dwt || ""}
                  </span>
                </div>
                <div className="add-vessel-form-group">
                  <label>Risk Score:</label>
                  <div>
                    {riskScoreLoading ? (
                      <span className="risk-score-loading">
                        <span className="risk-score-spinner" />
                        Loading...
                      </span>
                    ) : getRiskScoreText(riskScore) !== null ? (
                      <span
                        className={`risk-score-value ${getRiskColor(riskScore)}`}
                        title="Click to view risk details"
                        style={{ cursor: "pointer" }}
                        onClick={() => setRiskDialogOpen(true)}
                      >
                        {getRiskScoreText(riskScore)}
                      </span>
                    ) : (
                      <span className="risk-score-na">N/A</span>
                    )}
                  </div>
                </div>
              </div>
              <div className="add-vessel-add-btn-row">
                <button
                  className="add-vessel-add-btn"
                  type="submit"
                  disabled={!selectedVessel || loading}
                >
                  {loading ? "Adding..." : "Add Vessel"}
                </button>
              </div>
            </form>
          )}
          <div className="add-vessel-cancel-btn-row">
            <button className="add-vessel-cancel-btn" onClick={onCancel}>
              Cancel
            </button>
          </div>
          <RiskScoreDetailsModal
            open={riskDialogOpen}
            details={riskScoreJson ? riskScoreJson.data : null}
            onClose={() => setRiskDialogOpen(false)}
          />
        </div>
      </div>
    </div>
  );
};

export default AddVessel;
